import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import { useLayoutContext } from '../../context';
import styles from './sidebar-nav-item.module.scss';

const SIDEBAR_LEVEL_VARIABLE = '--ixf-sidebar-item-level';

const defaultActiveOptions = { includeSearch: false } as const;
const activeLinkProps = { className: styles.isActive } as const;

export const SIDEBAR_ACTIVE_TRANSITION = { duration: 0.2, type: 'tween' } as const;

export interface SidebarNavItemType<TLink extends string = string> {
  affix?: ReactNode;
  id: string;
  label: string;
  layoutGroupId?: string;
  prefix?: ReactNode;
  to: TLink;
  tooltipLabel?: string;
}

export interface SidebarNavItemGroup {
  id: string;
  items: SidebarNavItemType[];
  label?: string;
}

interface SidebarNavItemProps extends SidebarNavItemType {
  level: number;
}

export function SidebarNavItem({ affix, id, label, layoutGroupId = 'sidebar-nav-item', level, prefix, to, tooltipLabel }: SidebarNavItemProps) {
  const { handleCloseMobileNav, collapsedSidebarStatus, isMobile, showCollapsedSidebar } = useLayoutContext();

  const style = useMemo(
    () =>
      ({
        [SIDEBAR_LEVEL_VARIABLE]: level,
      }) as CSSProperties,
    [level],
  );

  const base = (
    <Link
      activeOptions={defaultActiveOptions}
      activeProps={activeLinkProps}
      className={clsx(styles.interactive, styles[`state-${collapsedSidebarStatus}`])}
      onClick={handleCloseMobileNav}
      to={to}
    >
      {({ isActive }) => (
        <>
          {isActive ? (
            <motion.div className={styles.activeIndicator} layoutId={`${layoutGroupId}-${level}`} transition={SIDEBAR_ACTIVE_TRANSITION} />
          ) : null}

          <HorizontalStack blockAlign="center" className={styles.linkContent} gap={2}>
            {prefix ? <div className={styles.prefix}>{prefix}</div> : null}
            {!showCollapsedSidebar || collapsedSidebarStatus === 'expanding' ? (
              <HorizontalStack blockAlign="center" className={styles.labelContainer} gap={2}>
                <span>{label}</span>
              </HorizontalStack>
            ) : null}
            {!showCollapsedSidebar && (isMobile || collapsedSidebarStatus === 'expanded') && affix ? <div className={styles.affix}>{affix}</div> : null}
          </HorizontalStack>
        </>
      )}
    </Link>
  );

  return (
    <li className={styles.sidebarNavItem} id={id} style={style}>
      {showCollapsedSidebar ? (
        <Tooltip activator={base} side="right" sideOffset={8} withArrow={false}>
          {tooltipLabel || label}
        </Tooltip>
      ) : (
        base
      )}
    </li>
  );
}
